import { Contact } from '@/infra/types/domain';
import { ContactsState } from './ducks';

type StateWithContacts = { contacts: ContactsState } | ContactsState;

function getContactsState(state: StateWithContacts): ContactsState {
  return 'contacts' in state && 'searchResults' in (state as any).contacts
    ? (state as { contacts: ContactsState }).contacts
    : (state as ContactsState);
}

export const selectContacts = (state: StateWithContacts): Contact[] =>
  getContactsState(state).contacts;

export const selectSearchResults = (state: StateWithContacts): Contact[] =>
  getContactsState(state).searchResults;

export const selectContactsLoading = (state: StateWithContacts): boolean =>
  getContactsState(state).loading;

export const selectSearchLoading = (state: StateWithContacts): boolean =>
  getContactsState(state).searchLoading;

export const selectContactsError = (state: StateWithContacts): string | null =>
  getContactsState(state).error;

/**
 * Returns the lifedb contact IDs that have a most-recent completed
 * enhance_contact task whose result_summary includes suggestion_ids.
 *
 * This is a _candidate_ signal — we treat the presence of suggestion_ids in
 * the task's result as "there may be pending suggestions to review." Some of
 * those suggestions may have been applied or rejected since; we tolerate the
 * false positives to avoid N round-trips on page load. If the list of
 * candidates becomes noisy we can either add a bulk pending-suggestions
 * endpoint or filter here by fetching per-contact statuses.
 */
export const selectContactIdsToReview = (state: any): string[] => {
  const bg = state?.backgroundTasks;
  if (!bg) return [];
  const bySubject: Record<string, { startedAt: string; hasSuggestions: boolean; status: string }> = {};
  for (const id of bg.order || []) {
    const t = bg.items[id];
    if (!t) continue;
    if (t.task_type !== 'enhance_contact') continue;
    if (!t.subject_id) continue;
    // Keep only the latest task per subject (order is newest-first).
    if (bySubject[t.subject_id]) continue;
    const rs = t.result_summary as { suggestion_ids?: string[] } | undefined;
    const hasSuggestions = Array.isArray(rs?.suggestion_ids) && (rs!.suggestion_ids!.length > 0);
    bySubject[t.subject_id] = {
      startedAt: t.started_at,
      hasSuggestions,
      status: t.status,
    };
  }
  const out: string[] = [];
  for (const [subjectId, info] of Object.entries(bySubject)) {
    if (info.status !== 'completed') continue;
    if (!info.hasSuggestions) continue;
    out.push(subjectId);
  }
  return out;
};
